import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { Link as RRLink } from 'react-router-dom';
import chroma from 'chroma-js';
import { Text } from '../../typography';
import { LinkInternalType, ActionType } from '../../types';
import { SPACINGS } from '../../utils/style';
import theme from '../../utils/theme/theme';

type BUTTON_STYLE_TYPE = 'outline' | 'primary' | 'outline-round';
type BUTTON_SIZE = 'sm' | 'base' | 'lg';

const sizesMap = {
  sm: SPACINGS[8],
  base: SPACINGS[10],
  lg: SPACINGS[12],
};

type ButtonStyledProps = {
  accent: string;
  styleType: BUTTON_STYLE_TYPE;
  size: BUTTON_SIZE;
  background?: string;
  useShades?: boolean;
};

function getColorShades(
  color: string,
  type: BUTTON_STYLE_TYPE,
  background?: string,
  useShades?: boolean
) {
  if (type === 'outline' || type === 'outline-round') {
    let hoverColor = background ?? theme.colors.primary92w;
    if(background && useShades){
      hoverColor = chroma.mix(color, background, 0.55).hex()
    } 

    return {
      default: color ?? theme.colors.primary,
      bg: background ?? 'transparent',
      hover: hoverColor,
      disabled: 'transparent',
      disabledBorder: theme.colors.black90w,
    };
  }

  const backgroundColor = background ?? color;
  return {
    default: color,
    bg: color,
    hover: useShades ? chroma(backgroundColor).darken(0.8).hex() : backgroundColor,
    disabled: theme.colors.black90w,
    disabledBorder: theme.colors.black90w,
  };
}


function getBorderRadius(type: BUTTON_STYLE_TYPE){
  if (type === 'outline-round') return '100%';
  return '4px';
}

function getLinkPadding(type: BUTTON_STYLE_TYPE){
  if (type === 'outline-round') return `${SPACINGS[2]}`;

  return `0 ${SPACINGS[5]}`;
}

function getLinkWidth(type: BUTTON_STYLE_TYPE, size: BUTTON_SIZE){
  if (type === 'outline-round') return `${sizesMap[size]}`;

  return "100%";
}

const ButtonStyled = styled.button<ButtonStyledProps>`
  display: inline-flex;
  align-items: center;
  user-select: none;
  height: ${(p) => sizesMap[p.size]};
  padding: 0 ${SPACINGS[5]};
  border-radius: ${(p) =>
    getBorderRadius(p.styleType)};

  background-color: ${(p) =>
    getColorShades(p.accent, p.styleType, p.background, p.useShades).bg};

  &:not(:disabled) {
    outline: none;
    cursor: pointer;
    border: 1px solid
      ${(p) => getColorShades(p.accent, p.styleType, p.background, p.useShades).default};

    &:hover,
    &:active,
    &:focus {
      background-color: ${(p) =>
        getColorShades(p.accent, p.styleType, p.background, p.useShades).hover};
      border: 1px solid
        ${(p) => getColorShades(p.accent, p.styleType, p.background, p.useShades).default};
      outline: none;
    }
  }

  &:disabled {
    border: 1px solid
      ${(p) =>
        getColorShades(p.accent, p.styleType, p.background, p.useShades).disabledBorder};
    background-color: ${(p) =>
      getColorShades(p.accent, p.styleType, p.background, p.useShades).disabled};
  }
`;

const ButtonLinkStyled = styled.span<ButtonStyledProps>`
  display: inline-flex;
  align-items: center;
  border-radius: ${(p) =>
    getBorderRadius(p.styleType)};

  & a {
    display: inline-flex;
    align-items: center;
    user-select: none;
    width: ${(p) => getLinkWidth(p.styleType, p.size)};
    height: ${(p) => sizesMap[p.size]};
    padding: ${(p) =>
      getLinkPadding(p.styleType)};    
    background-color: ${(p) =>
      getColorShades(p.accent, p.styleType, p.background, p.useShades).bg};

    outline: none;
    cursor: pointer;
    border: 1px solid
      ${(p) => getColorShades(p.accent, p.styleType, p.background, p.useShades).default};
    text-decoration: none;
    box-sizing: border-box;

    &:hover,
    &:focus,
    &:active {
      background-color: ${(p) =>
        getColorShades(p.accent, p.styleType, p.background, p.useShades).hover};
      border: 1px solid
        ${(p) => getColorShades(p.accent, p.styleType, p.background, p.useShades).default};
      outline: none;
      text-decoration: none;
    }
  }
`;

const IconWrapper = styled.div<{ position: 'left' | 'right' }>`
  display: inline-flex;
  height: ${SPACINGS[6]};
  margin: ${(p) =>
    p.position === 'left' ? `0 ${SPACINGS[3]} 0 0` : `0 0 0 ${SPACINGS[3]}`};
  justify-content: center;
  align-items: center;
`;

type ButtonPropsBase = {
  children: React.ReactNode;
  disabled?: boolean;
  styleType?: BUTTON_STYLE_TYPE;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  style?: React.CSSProperties;
  testid?: string;
  accent?: string;
  background?: string;
  size?: BUTTON_SIZE;
  useShades?: boolean;
};

type ButtonProps =
  | (ButtonPropsBase & ActionType)
  | (ButtonPropsBase & LinkInternalType)
  | (ButtonPropsBase & { type: 'submit'; form?: string });

  
const Button = React.forwardRef((props: ButtonProps, ref: any) => {
  const {
    children,
    styleType = 'primary',
    size = 'base',
    disabled,
    iconLeft,
    iconRight,
    style,
    testid,
    accent,
    background,
    useShades = true
  } = props;

  const theme = useTheme();

  const textStyle = React.useMemo(
    () => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      color: disabled ? theme.colors.black40w : style?.color ?? theme.colors.white,
    }),
    [style?.color, disabled]
  );

  const outlineTextStyle = React.useMemo(
    () => ({
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      color: style?.color,
    }),
    [style?.color]
  );

  if (props.type === 'submit') {
    return (
      <ButtonStyled
        accent={accent ?? theme.colors.primary}
        background={background}
        form={props.form}
        style={style}
        styleType={styleType}
        ref={ref}
        disabled={disabled}
        aria-disabled={disabled}
        data-testid={testid}
        type="submit"
        size={size}
        useShades={useShades}
      >
        <Text
          style={styleType === 'outline' ? outlineTextStyle : textStyle}
          size="sm"
          margin="none"
          transform="uppercase"
          opacity={disabled ? 50 : 100}
          align="center"
        >
          {iconLeft && <IconWrapper position="left">{iconLeft}</IconWrapper>}
          {children}
          {iconRight && <IconWrapper position="right">{iconRight}</IconWrapper>}
        </Text>
      </ButtonStyled>
    );
  }

  if (props.type === 'action') {
    return (
      <ButtonStyled
        accent={accent ?? theme.colors.primary}
        background={background}
        type="button"
        style={style}
        styleType={styleType}
        onClick={props.onClick}
        ref={ref}
        disabled={disabled}
        aria-disabled={disabled}
        data-testid={testid}
        size={size}
        useShades={useShades}
      >
        <Text
          style={styleType === 'outline' ? outlineTextStyle : textStyle}
          size="sm"
          margin="none"
          transform="uppercase"
          opacity={disabled ? 50 : 100}
          align="center"
        >
          {iconLeft && <IconWrapper position="left">{iconLeft}</IconWrapper>}
          {children}
          {iconRight && <IconWrapper position="right">{iconRight}</IconWrapper>}
        </Text>
      </ButtonStyled>
    );
  }

  if ((props.type === 'link' || props.type === 'link-internal') && !disabled) {
    const Link = props.linkComponent ?? RRLink;

    return (
      <ButtonLinkStyled
        accent={accent ?? theme.colors.primary}
        background={background}
        style={style}
        styleType={styleType}
        ref={ref}
        data-testid={testid}
        size={size}
        useShades={useShades}
      >
        <Link aria-disabled={disabled} to={props.to} style={{ borderRadius: getBorderRadius(styleType) }}>
          <Text
            style={styleType === 'outline' ? outlineTextStyle : textStyle}
            size="sm"
            margin="none"
            transform="uppercase"
            opacity={disabled ? 50 : 100}
            align="center"
          >
            {iconLeft && <IconWrapper position="left">{iconLeft}</IconWrapper>}
            {children}
            {iconRight && (
              <IconWrapper position="right">{iconRight}</IconWrapper>
            )}
          </Text>
        </Link>
      </ButtonLinkStyled>
    );
  }

  if ((props.type === 'link' || props.type === 'link-internal') && disabled) {
    return (
      <ButtonStyled
        accent={accent ?? theme.colors.primary}
        background={background}
        style={style}
        styleType={styleType}
        ref={ref}
        disabled={disabled}
        aria-disabled={disabled}
        data-testid={testid}
        size={size}
        useShades={useShades}
      >
        <Text
          style={styleType === 'outline' ? outlineTextStyle : textStyle}
          size="sm"
          margin="none"
          transform="uppercase"
          opacity={disabled ? 50 : 100}
          align="center"
        >
          {iconLeft && <IconWrapper position="left">{iconLeft}</IconWrapper>}
          {children}
          {iconRight && <IconWrapper position="right">{iconRight}</IconWrapper>}
        </Text>
      </ButtonStyled>
    );
  }

  return null;
});

export default React.memo(Button);
